import React, { useState } from 'react';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { useUpdateReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';

import ApiError from '~/types/ApiError';
import type { Lens } from '~/types/leLens';

type Props = {
  /** The saved definition when editing starts, including its conflict-checking version. */
  lens: Lens;
  onClose: () => void;
};

export default function EditLensDetailsDialog({ lens, onClose }: Props) {
  const updateLens = useUpdateReportingLeLens();
  const [title, setTitle] = useState(lens.title);
  const [description, setDescription] = useState(lens.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [hasConflict, setHasConflict] = useState(false);

  const save = async () => {
    if (!title.trim() || updateLens.isPending || hasConflict) return;
    setError(null);
    try {
      await updateLens.mutateAsync({
        id: lens.id,
        title: title.trim(),
        description: description.trim() || null,
        spec: lens.spec,
        version: lens.version,
        refetchData: false,
        quietFailure: true,
        successMessage: 'Lens details updated',
      });
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setHasConflict(true);
        setError(
          'This Lens changed elsewhere, or your edit access changed. Close this dialog and reopen it to try again.',
        );
      } else {
        setError(error instanceof Error ? error.message : 'Could not save Lens details. Try again.');
      }
    }
  };

  return (
    <Dialog
      aria-labelledby="edit-lens-details-title"
      fullWidth
      maxWidth="sm"
      onClose={updateLens.isPending ? undefined : onClose}
      open
    >
      <DialogTitle id="edit-lens-details-title">Edit Lens details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            autoFocus
            disabled={updateLens.isPending || hasConflict}
            fullWidth
            inputProps={{ maxLength: 200 }}
            label="Lens name"
            onChange={(event) => setTitle(event.target.value)}
            required
            value={title}
          />
          <TextField
            disabled={updateLens.isPending || hasConflict}
            fullWidth
            label="Description"
            minRows={3}
            multiline
            onChange={(event) => setDescription(event.target.value)}
            value={description}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={updateLens.isPending} onClick={onClose}>
          {hasConflict ? 'Close' : 'Cancel'}
        </Button>
        <Button disabled={updateLens.isPending || !title.trim() || hasConflict} onClick={save} variant="contained">
          {updateLens.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
